import { useEffect, useMemo, useRef } from 'react';

import { StructuredMeasurements } from '@restDataAccess';
import { MeasurementRange } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';

import { AutoResizeHighChart } from '@/components';
import Highcharts from '@/components/chart/highcharts';

import { useChartOptionsWithLocale } from '../../hooks';
import { AggregatedChartConfigType, AggregatedChartYAxisOptions } from '../../types.ts';
import { useConfigureAggregatedHighChart } from './aggregatedHighChart/useConfigureAggregatedHighChart.ts';

interface AggregatedHighChartPreDataProps {
  /**
   * Measurement series already loaded by the parent (e.g. `LiceCountWidget` via
   * `useDataFromConfig`). This component does not fetch — it only configures and renders the chart.
   */
  allMeasurements: StructuredMeasurements<MeasurementRange> | undefined;
  config: AggregatedChartConfigType;
  hasMeasurements: boolean;
  /** From the same `useDataFromConfig` call that produced `allMeasurements`. */
  isLive: boolean;
  /** From the same `useDataFromConfig` call that produced `allMeasurements`. */
  isTimeSeriesNormalized: boolean;
  onChartNameChange?: (chartName: string | undefined) => void;
  chartName?: string;
  yAxisOptions?: AggregatedChartYAxisOptions;
  lowDataCaptureThreshold?: number;
}

/**
 * Aggregated Highcharts view for parents that already own the measurement query.
 * `AggregatedHighChart` wraps this after fetching; shared-data parents (e.g. `LiceCountWidget`)
 * render it directly to avoid a second set of measurement-range requests.
 */
export const AggregatedHighChartPreData = ({
  config,
  onChartNameChange,
  chartName,
  yAxisOptions,
  lowDataCaptureThreshold,
  allMeasurements,
  hasMeasurements,
  isLive,
  isTimeSeriesNormalized,
}: AggregatedHighChartPreDataProps) => {
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);

  const { tickInterval, minorTickInterval, minRange, floor, ceiling, maxPadding } =
    yAxisOptions || {};
  const { plot, sensors } = config;

  useEffect(() => {
    onChartNameChange?.(chartName);
  }, [chartName, onChartNameChange]);

  useConfigureAggregatedHighChart({
    allMeasurements,
    config,
    hasMeasurements,
    highChartsRef,
    isLive,
    isTimeSeriesNormalized,
    ceiling,
    floor,
    lowDataCaptureThreshold,
    maxPadding,
    minRange,
    minorTickInterval,
    tickInterval,
  });

  const chartOptionsWithLocale = useChartOptionsWithLocale();
  const chartOptionsWithTimeFormat = useMemo(
    () => ({
      ...chartOptionsWithLocale,
      tooltip: {
        shared: sensors.length === 1 && plot.length === 1,
      },
    }),
    [chartOptionsWithLocale, plot.length, sensors.length]
  );

  return (
    <AutoResizeHighChart
      highcharts={Highcharts}
      options={chartOptionsWithTimeFormat}
      highChartsRef={highChartsRef}
    />
  );
};
